// import { useTranslation } from 'react-i18next';
// import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
// import { APPROVE_BTN_LINK } from '@/constants/links';
// import classNames from 'classnames';
import './index.less';

import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { Tooltip } from '@/components/ToolTip';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import { FAQ_LINKS } from '@/constants/links';
import { useTokenBalance } from '@/features/balance/hook';
import { useIsIpBlocked } from '@/features/global/hooks';
import { approveToken, checkTokenAllowance } from '@/features/user/actions';
// import { Button } from '@/components/Button';
import { useAppDispatch } from '@/hooks';
import { useGa } from '@/hooks/useGa';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { IApproveButtonProps } from '@/types/button';
// import { useChainTokenAllowanceApproved, useChainTokenAllowance } from '@/features/user/hooks';
import { GlobalModalType } from '@/constants';
import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useIsEnableAgent } from '@/features/agent/hook';
import { useFetchUserTokenBalanceList } from '@/features/balance/query';
import { useToggleModal } from '@/features/global/hooks';
import { useWalletConnectStatus } from '@/features/wallet/hook';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useSwitchNetwork } from '@/hooks/web3/useSwitchConnectorNetwork';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { WALLET_CONNECT_STATUS } from '@/types/wallet';
import { GaCategory } from '@/utils/analytics';
import { isNativeTokenAddr } from '@/utils/token';
import _ from 'lodash';

export function ApproveButtonComponent({
  spenderAddress,
  className,
  loading,
  afterApproved,
  disabled,
  marginToken,
  children,
  titleNotApproved,
  amount,
  noNeedApprove = false,
  maxAmount,
  showChainIcon = false,
  disabledOvervided = false,
  ...props
}: IApproveButtonProps): JSX.Element {
  const { t } = useTranslation();
  const { isMobile } = useMediaQueryDevice();
  const ipBlocked = useIsIpBlocked();
  const isEnableAgent = useIsEnableAgent();
  const notification = useTxNotification();
  const walletConnectStatus = useWalletConnectStatus();
  const toggleWalletModal = useToggleModal(GlobalModalType.Wallet);

  const toggleEnableAgentModal = useToggleModal(GlobalModalType.ENABLE_AGENT);
  const { switchWalletNetwork } = useSwitchNetwork();
  const userAddr = useUserAddr();
  const chainId = useChainId();

  const { data, isFetched } = useFetchUserTokenBalanceList(chainId, userAddr, spenderAddress);
  const tokenAllowance = useMemo(() => {
    if (data) {
      return data.find((item) => item.address === marginToken?.address);
    }
    return undefined;
  }, [data, marginToken?.address]);

  const allowanceTooLow = useMemo(() => {
    if (marginToken?.address && isNativeTokenAddr(marginToken.address)) {
      return false;
    }
    return tokenAllowance?.allowance && tokenAllowance?.allowance.lt(WrappedBigNumber.from(amount || 0));
  }, [marginToken?.address, tokenAllowance, amount]);

  const titleNotApprovedAdjusted = useMemo(
    () =>
      titleNotApproved ||
      t('common.approveBtn.approveLine', { symbol: marginToken?.symbol }) +
        (isMobile ? '' : t('common.approveBtn.continue')),
    [isMobile, marginToken?.symbol, t, titleNotApproved],
  );
  const dispatch = useAppDispatch();

  const tokenBalance = useTokenBalance(marginToken?.address, chainId);
  const balanceInsuf = useMemo(() => {
    if (maxAmount) {
      return maxAmount && amount && maxAmount.lt(amount);
    }
    return amount && tokenBalance.wadValue.lt(amount);
  }, [amount, maxAmount, tokenBalance.wadValue]);
  const signer = useWalletSigner();
  const provider = useAppProvider();
  const sdk = useSDK(chainId);
  //const isBlacklisted = useIsBlacklistedFromStore();
  const gaEvent = useGa();
  const disableBtn = useMemo(() => {
    if (allowanceTooLow) {
      return !allowanceTooLow && disabled;
    }
    return disabled;
  }, [disabled, allowanceTooLow]);
  const onClickButton = useCallback(async () => {
    if (walletConnectStatus === WALLET_CONNECT_STATUS.UN_CONNECT) {
      toggleWalletModal(true);
      return;
    }
    if (walletConnectStatus === WALLET_CONNECT_STATUS.WRONG_NETWORK) {
      chainId && switchWalletNetwork(chainId);
      return;
    }
    if (chainId && !DAPP_CHAIN_CONFIGS[chainId]?.disable1CT && !isEnableAgent) {
      toggleEnableAgentModal(true);
      return;
    }

    if (disableBtn || !isFetched) {
      return;
    }
    if (!allowanceTooLow || noNeedApprove) {
      afterApproved();
    } else {
      marginToken &&
        chainId &&
        userAddr &&
        signer &&
        provider &&
        amount &&
        sdk &&
        dispatch(
          approveToken({
            chainId,
            signer,
            provider,
            userAddr: userAddr,
            marginToken,
            amount,
            spenderAddress,
            sdk,
            sendTxNotification: notification?.open,
          }),
        );
      gaEvent({
        category: GaCategory.TRADE_PAIR_CARD_WRAPPER,
        action: 'Trade-Click on Approve Token',
      });
    }
  }, [
    walletConnectStatus,
    isEnableAgent,
    disableBtn,
    isFetched,
    allowanceTooLow,
    noNeedApprove,
    toggleWalletModal,
    chainId,
    switchWalletNetwork,
    toggleEnableAgentModal,
    afterApproved,
    marginToken,
    userAddr,
    signer,
    provider,
    amount,
    sdk,
    dispatch,
    spenderAddress,
    notification?.open,
    gaEvent,
  ]);
  // always check the approval status of the underlying
  useEffect(() => {
    if (userAddr && chainId && marginToken?.address && sdk) {
      dispatch(
        checkTokenAllowance({
          userAddress: userAddr,
          chainId,
          marginToken: marginToken,
          spenderAddress,
          sdk,
        }),
      );
    }
    // must use margintoken.address otherwise it will trigger infinite loop
  }, [dispatch, userAddr, chainId, provider, marginToken?.address, spenderAddress, sdk]);

  return (
    <Tooltip showOnMobile={true} title={ipBlocked ? t('common.ipBlocker.tooltip') : undefined}>
      <Button
        {..._.omit(props, ['unConnectedText'])}
        disabled={disabledOvervided || disableBtn || balanceInsuf || ipBlocked}
        type="primary"
        className={classNames(className, 'syn-approve-btn')}
        loading={loading || (noNeedApprove ? false : !isFetched)}
        chainIconProps={showChainIcon && chainId ? { chainId, width: 18, marginRight: 4 } : undefined}
        onClick={onClickButton}
        //isBlocked={props.isBlocked !== undefined ? props.isBlocked : isBlacklisted}
      >
        {walletConnectStatus === WALLET_CONNECT_STATUS.UN_CONNECT ? (
          t('common.connectWal')
        ) : walletConnectStatus === WALLET_CONNECT_STATUS.WRONG_NETWORK ? (
          t('common.global.wrongNet')
        ) : chainId && !DAPP_CHAIN_CONFIGS[chainId]?.disable1CT && !isEnableAgent ? (
          t('common.agent.EnableTrading')
        ) : !allowanceTooLow || balanceInsuf || noNeedApprove ? (
          children
        ) : (
          <>
            {titleNotApprovedAdjusted}
            <LeanMoreToolTip link={FAQ_LINKS.APPROVE_FAQ} title={t('common.approveBtn.tooltip')} />
          </>
        )}
      </Button>
    </Tooltip>
  );
}
export const ApproveWrapButton = React.memo(ApproveButtonComponent);
